import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import type { AppPreferences, Card, Deck, Folder, UserProfile } from '../../types/entities';
import { useAuth } from './AuthProvider';

// Firestore e os repositórios entram por import dinâmico: o shell e a tela de login
// continuam fora do orçamento de JS inicial, e o SDK só é baixado depois de autenticar.
async function loadDataLayer() {
  const [library, settings, stats, firestoreService, firestore] = await Promise.all([
    import('../../features/library/library.repository'),
    import('../../features/settings/settings.repository'),
    import('../../features/stats/stats.repository'),
    import('../../services/firebase/firestore'),
    import('firebase/firestore'),
  ]);
  return { ...library, ...settings, ...stats, getFirestoreService: firestoreService.getFirestoreService, Timestamp: firestore.Timestamp };
}

export interface LibraryState {
  /** Pastas ativas, ordenadas. */
  readonly folders: readonly Folder[];
  /** Decks ativos com `cardCount` (null quando ainda não calculado). */
  readonly decks: readonly Deck[];
  /** Cards vencidos ou novos (sem dueAt), prontos para revisão agora. */
  readonly dueCards: readonly Card[];
  readonly preferences: AppPreferences | null;
  readonly profile: UserProfile | null;
  /** Total de cards somando os contadores dos decks; null se algum deck ainda não tem contador. */
  readonly cardCount: number | null;
  readonly dueCount: number;
  readonly dueByDeck: ReadonlyMap<string, number>;
  /** false até o primeiro snapshot (cache ou rede) de decks, pastas e vencidos chegar. */
  readonly ready: boolean;
  readonly fromCache: boolean;
  readonly error: string | null;
}

const LibraryContext = createContext<LibraryState | null>(null);

const empty: LibraryState = { folders: [], decks: [], dueCards: [], preferences: null, profile: null, cardCount: null, dueCount: 0, dueByDeck: new Map(), ready: false, fromCache: false, error: null };

// A consulta de vencidos usa um instante fixo; renovar de tempos em tempos faz cards que
// venceram enquanto o app estava aberto aparecerem sem recarregar.
const DUE_REFRESH_MS = 5 * 60_000;

/**
 * Fonte única de pastas, decks, vencidos, preferências e perfil para todas as rotas.
 * Carrega uma vez por sessão com listeners em tempo real, então trocar de tela não vai à rede
 * e qualquer gravação reflete em todas as abas sem eventos manuais.
 */
export function LibraryProvider({ children }: PropsWithChildren): React.JSX.Element {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const [folders, setFolders] = useState<readonly Folder[] | null>(null);
  const [decks, setDecks] = useState<readonly Deck[] | null>(null);
  const [dueCards, setDueCards] = useState<readonly Card[] | null>(null);
  const [preferences, setPreferences] = useState<AppPreferences | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dueSince, setDueSince] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setDueSince(Date.now()), DUE_REFRESH_MS);
    const onVisible = (): void => { if (document.visibilityState === 'visible') setDueSince(Date.now()); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  useEffect(() => {
    setFolders(null); setDecks(null); setDueCards(null); setPreferences(null); setProfile(null); setError(null);
    if (!uid) return;
    let active = true;
    let stops: (() => void)[] = [];
    const fail = (reason: Error): void => setError(reason.message);
    void loadDataLayer().then((data) => {
      if (!active) return;
      const firestore = data.getFirestoreService();
      stops = [
        data.watchFolders(firestore, uid, { next: (snapshot) => { setFolders(snapshot.data); setFromCache(snapshot.fromCache); }, error: fail }),
        data.watchDecks(firestore, uid, { next: (snapshot) => { setDecks(snapshot.data); setFromCache(snapshot.fromCache); }, error: fail }),
        data.watchPreferences(firestore, uid, setPreferences, fail),
        data.watchProfile(firestore, uid, setProfile, fail),
      ];
    }).catch(fail);
    return () => { active = false; stops.forEach((stop) => stop()); };
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    let active = true;
    let stop: (() => void) | null = null;
    void loadDataLayer().then((data) => {
      if (!active) return;
      stop = data.watchDueCards(data.getFirestoreService(), uid, data.Timestamp.fromMillis(dueSince), { next: (snapshot) => setDueCards(snapshot.data), error: (reason) => setError(reason.message) });
    }).catch((reason: Error) => setError(reason.message));
    return () => { active = false; stop?.(); };
  }, [dueSince, uid]);

  // As telas de Configurações e Perfil continuam avisando por evento; manter os dois caminhos
  // deixa a UI imediata mesmo antes do snapshot do Firestore chegar.
  useEffect(() => {
    const receive = (event: Event): void => setPreferences((event as CustomEvent<AppPreferences>).detail);
    window.addEventListener('echo-preferences-changed', receive);
    return () => window.removeEventListener('echo-preferences-changed', receive);
  }, []);

  const value = useMemo<LibraryState>(() => {
    if (!uid) return empty;
    const activeDecks = decks ?? [];
    const deckIds = new Set(activeDecks.map((deck) => deck.id));
    const due = (dueCards ?? []).filter((card) => deckIds.has(card.deckId));
    const dueByDeck = new Map<string, number>();
    due.forEach((card) => dueByDeck.set(card.deckId, (dueByDeck.get(card.deckId) ?? 0) + 1));
    const cardCount = activeDecks.every((deck) => typeof deck.cardCount === 'number') ? activeDecks.reduce((total, deck) => total + (deck.cardCount ?? 0), 0) : null;
    return {
      folders: folders ?? [],
      decks: activeDecks,
      dueCards: due,
      preferences,
      profile,
      cardCount,
      dueCount: due.length,
      dueByDeck,
      ready: folders !== null && decks !== null && dueCards !== null,
      fromCache,
      error,
    };
  }, [decks, dueCards, error, folders, fromCache, preferences, profile, uid]);

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
}

export function useLibrary(): LibraryState {
  const context = useContext(LibraryContext);
  if (!context) throw new Error('useLibrary deve ser usado dentro de LibraryProvider.');
  return context;
}
